<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getImages = async () => {
    const images = await AssetsService.getAllImages();
    return images.map((image) => ({
      label: image.filename,
      value: image.filename,
      ...image
    }));
  };

  const onDeleteImage = async (image) => {
    const deleteResponse = await AssetsService.deleteImage(image);

    if (deleteResponse?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: image.filename } }), 'error');
      throw new Error($format('label.FAILURE.DELETE_IMAGE'));
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: image.filename } }));

    step.data = {
      id: image.filename,
      imageData: {
        filename: image.filename,
        filesize: image.filesize,
        hash: image.hash,
        inline: image.inline,
        metadata: image.metadata,
        mimetype: image.mimetype,
        rulename: image.rulename
      }
    };
  };

  const rollbackDeleteImage = async () => {
    if (!step.data?.imageData) {
      return;
    }

    $appStore.loading = true;

    try {
      const response = await AssetsService.addImage(step.data.imageData);

      if (response?.status >= 400) {
        throw new Error($format('label.FAILURE.ROLLBACK_IMAGE_DELETION'));
      }
    } catch (error) {
      console.error('Failed to rollback image deletion:', error);
    } finally {
      $appStore.loading = false;
    }
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  rollbackFunction={rollbackDeleteImage}
  getFunction={getImages}
  deleteFunction={onDeleteImage}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      <div class="image-preview">
        {#if item.hash}
          <div class="image-thumbnail">
            <img src="https://assets.opensense.com/general/{item.hash}" alt={item.filename} />
          </div>
        {/if}
        <div class="image-details">
          <div class="detail-item">
            <span class="label">{$format('label.FILENAME')}:</span>
            <span class="value image-name">{item.filename}</span>
          </div>
          {#if item.filesize}
            <div class="detail-item">
              <span class="label">{$format('label.SIZE')}:</span>
              <span class="value">{(item.filesize / 1024).toFixed(2)} KB</span>
            </div>
          {/if}
          {#if item.mimetype}
            <div class="detail-item">
              <span class="label">{$format('label.TYPE')}:</span>
              <span class="value">{item.mimetype}</span>
            </div>
          {/if}
          {#if item.inline}
            <div class="detail-item">
              <span class="label">{$format('label.INLINE')}:</span>
              <span class="value status-active">{$format('label.YES')}</span>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .image-preview {
    background-color: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: var(--margin-base);
    padding: var(--padding-base);

    .image-thumbnail {
      align-items: center;
      display: flex;
      justify-content: center;
      width: 100%;

      img {
        border: 1px solid var(--color-gray-200);
        border-radius: var(--border-radius-small);
        max-height: 150px;
        max-width: 100%;
        object-fit: contain;
      }
    }

    .image-details {
      display: flex;
      flex-direction: column;
      gap: var(--margin-small);
      width: 100%;

      .detail-item {
        display: flex;
        gap: var(--margin-small);

        .label {
          color: var(--color-gray-600);
          flex-shrink: 0;
          font-weight: var(--font-weight-medium);
        }

        .value {
          color: var(--color-gray-900);
          word-break: break-all;

          &.image-name {
            font-weight: var(--font-weight-medium);
          }

          &.status-active {
            color: var(--color-green-600);
            font-weight: var(--font-weight-medium);
          }
        }
      }
    }
  }
</style>
